import React, { useState, KeyboardEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { routeGroups, getBottomNavItems, RoutePaths } from "../../enumStrings";
import { UserType } from "../../graphql/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faDesktop,
  faDollarSign,
  faChartBar,
  faGear,
  faRightFromBracket,
  faChevronDown,
  faChevronUp,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  faUser,
  faCalendar,
  faEnvelope,
  faQuestionCircle,
} from "@fortawesome/free-regular-svg-icons";
import cmpLogo from "../../images/cmp_logo.png";
import cpaLogo from "../../images/cpa_logo.png";
import useGetChamberFromUserIdInAuth from "../../hooks/useGetChamberFromUserIdInAuth";
import { addHttpToUrl } from "../../utils/addHttpToUrl";
import { useHandleLogout } from "../../hooks/useHandleLogout";

type RouteType = {
  path: string;
  title?: string;
  isExternalLink?: boolean;
};

type NavbarProps = {
  onClose?: () => void;
};

const getIcon = (iconName: string): any => {
  switch (iconName) {
    case "home":
      return faHome;
    case "person":
      return faUser;
    case "calendar":
      return faCalendar;
    case "desktop":
      return faDesktop;
    case "email":
      return faEnvelope;
    case "attach_money":
      return faDollarSign;
    case "bar_chart":
      return faChartBar;
    case "card_giftcard":
      return "cpa_logo"; // Special case for Chamber Perks App
    case "settings":
      return faGear;
    case "question_circle":
      return faQuestionCircle;
    default:
      return faHome;
  }
};

const Navbar: React.FC<NavbarProps> = ({ onClose }) => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<string[]>([]);

  const toggleMenu = (title: string) => {
    if (openMenus.includes(title)) {
      setOpenMenus(openMenus.filter((menu) => menu !== title));
    } else {
      setOpenMenus([...openMenus, title]);
    }
  };

  const isMenuActive = (routes: RouteType[]) => {
    return routes.some((route) => location.pathname === route.path);
  };

  const isSubMenuActive = (path: string) => {
    return location.pathname === path;
  };

  // Use routeGroups for rendering since navBarConfigToShow is empty in this setup
  const navItems = routeGroups[UserType.ChamberAdmin];
  const bottomNav = getBottomNavItems(UserType.ChamberAdmin);

  const handleKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    callback: () => void
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      callback();
    }
  };

  const chamberInfo = useGetChamberFromUserIdInAuth();

  const handleLogout = useHandleLogout();

  const renderIcon = (icon: any) => {
    if (icon === "cpa_logo") {
      return (
        <img
          src={cpaLogo}
          alt="Chamber Perks App"
          className="w-[24px] h-[24px] mr-2"
        />
      );
    }
    return (
      <FontAwesomeIcon icon={icon} className="w-[24px] mr-2 text-gray-600" />
    );
  };

  const renderNavLink = (
    path: string,
    title: string,
    icon: any,
    isActive: boolean,
    isExternalLink?: boolean
  ) => {
    if (isExternalLink) {
      let pathToGoTo = path;

      if (path === RoutePaths.chamberAdmin_website_builder) {
        const websiteUrl = addHttpToUrl(
          chamberInfo?.chamberMemberProWebsiteUrl
        );

        if (!websiteUrl) {
          return null;
        }

        pathToGoTo = websiteUrl
          ? websiteUrl.endsWith("/")
            ? `${websiteUrl}admin`
            : `${websiteUrl}/admin`
          : "";
      }

      return (
        <a
          href={pathToGoTo}
          target="_blank"
          rel="noopener noreferrer"
          className={`py-3 px-5 flex items-center cursor-pointer font-semibold ${
            isActive ? "text-cpaGold" : "text-gray-700"
          } relative`}
        >
          {renderIcon(icon)}
          {title}
        </a>
      );
    }

    return (
      <Link
        to={path}
        className={`py-3 px-5 flex items-center cursor-pointer font-semibold ${
          isActive ? "text-cpaGold" : "text-gray-700"
        } relative`}
        tabIndex={0}
      >
        {renderIcon(icon)}
        {title}
      </Link>
    );
  };

  return (
    <div className="w-[260px] h-screen bg-white flex flex-col py-5 overflow-y-auto">
      <div className="px-5 pb-4 flex items-center justify-between">
        <div className="flex items-center">
          <img src={cmpLogo} alt="Chamber Member Pro" className="h-12" />
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden text-gray-500 hover:text-gray-700"
            aria-label="Close navigation"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {navItems.map((item, index) => {
          if (item.nestedRoute) {
            const { title, icon, routes } = item.nestedRoute;
            const isOpen = openMenus.includes(title);
            const isActive = isMenuActive(routes);

            return (
              <React.Fragment key={index}>
                <div
                  className={`py-3 px-5 flex items-center cursor-pointer font-semibold ${
                    isActive ? "text-cpaGold" : "text-gray-700"
                  } relative`}
                  onClick={() => toggleMenu(title)}
                  aria-label={`Toggle ${title} menu`}
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, () => toggleMenu(title))}
                >
                  {renderIcon(getIcon(icon))}
                  {title}
                  <div className="ml-auto">
                    <FontAwesomeIcon
                      className="text-gray-600"
                      icon={isOpen ? faChevronUp : faChevronDown}
                    />
                  </div>
                </div>

                <div className={isOpen ? "block" : "hidden"}>
                  {routes.map((route, subIndex) => (
                    <Link
                      key={subIndex}
                      to={route.path}
                      className={`py-2.5 px-5 pl-[53px] block ${
                        isSubMenuActive(route.path)
                          ? "text-cpaGold font-semibold"
                          : "text-gray-700"
                      } no-underline hover:bg-gray-50`}
                    >
                      {route.title}
                    </Link>
                  ))}
                </div>
              </React.Fragment>
            );
          } else if (
            item.route &&
            !item.route.hideInNavBar &&
            !item.route.showInBottomNav
          ) {
            const { title, icon, path, isExternalLink } = item.route;
            const isActive = isSubMenuActive(path);

            return renderNavLink(
              path,
              title || "",
              getIcon(icon || "home"),
              isActive,
              isExternalLink
            );
          }

          return null;
        })}
      </div>

      <div className="mb-3 pt-2">
        {bottomNav.map((item, index) => {
          const isActive = isSubMenuActive(item.path);
          return renderNavLink(
            item.path,
            item.title || "",
            getIcon(item.icon || ""),
            isActive,
            item.isExternalLink
          );
        })}
      </div>

      <div className="border-t border-gray-200 pt-4 px-5 flex items-center justify-between">
        <div className="flex flex-col">
          {/* <div className="font-medium">Jane Doe</div> */}
          <div className="text-xs text-gray-500">{chamberInfo?.name}</div>
        </div>
        <div
          className="text-gray-500 cursor-pointer hover:text-cpaGold"
          aria-label="Sign out"
          tabIndex={0}
          onClick={handleLogout}
        >
          <FontAwesomeIcon className="text-red-500" icon={faRightFromBracket} />
        </div>
      </div>
    </div>
  );
};

export default Navbar;
